/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Box, Card, CardContent, Checkbox, FormControl, FormControlLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@material-ui/styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { EXPENSES_BATCH_TYPES, ZERO_GUID } from 'modules/enumerations/enumerationIds';

const useStyles = makeStyles(() => ({
  card: { width: '100%', flexGrow: 1, flexDirection: 'row', justifyContent: 'space-between' },
  cardContainer: { maxWidth: '100% !important', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' },
  iconRow: { display: 'flex', flexDirection: 'row' }
}));

const theme = createTheme({ palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING } });

const RecurringPayablePaymentInfoCard = ({
  rpInfo,
  isReadOnly,
  updateRpInfo, 
  banks = [],
  addresses = []
}) => {
  const classes = useStyles();
  const rpi = rpInfo?.recurringPayableInformation ?? {};
  const banking = rpInfo?.bankingInformation ?? {};
  const address = rpInfo?.addressInformation ?? {};

  const patchRPI = changes =>
    updateRpInfo({
      ...rpInfo,
      recurringPayableInformation: { ...(rpInfo?.recurringPayableInformation ?? {}), ...changes }
    });

  const patchBanking = changes =>
    updateRpInfo({
      ...rpInfo,
      bankingInformation: {
        recurringPayableBankingId: banking?.recurringPayableBankingId ?? null,
        recurringPayableId: rpi?.recurringPayableId ?? banking?.recurringPayableId ?? null,
        bankId: banking?.bankId ?? null,
        ...(rpInfo?.bankingInformation ?? {}),
        ...changes
      }
    });

  const patchAddress = changes =>
    updateRpInfo({
      ...rpInfo,
      addressInformation: {
        recurringPayableAddressId: address?.recurringPayableAddressId ?? null,
        recurringPayableId: rpi?.recurringPayableId ?? address?.recurringPayableId ?? null,
        addressId: address?.addressId ?? null,
        internalNotes: address?.internalNotes ?? '',
        ...(rpInfo?.addressInformation ?? {}),
        ...changes
      }
    });

  const batchTypeId = ZERO_GUID;

  const BANK_FLOW = new Set(
    [EXPENSES_BATCH_TYPES.ACH_WIRE, EXPENSES_BATCH_TYPES.BILL_TO_EE_ACH_WIRE, EXPENSES_BATCH_TYPES.INTERNATIONAL].map(x =>
      String(x).toLowerCase()
    )
  );
  const CHECK_FLOW = new Set([EXPENSES_BATCH_TYPES.CHECK, EXPENSES_BATCH_TYPES.BILL_TO_EE_CHECK].map(x => String(x).toLowerCase()));
  const NO_PAYMENT_FLOW = new Set(
    [
      EXPENSES_BATCH_TYPES.AP_TRACKING,
      EXPENSES_BATCH_TYPES.EM_TRACKING,
      EXPENSES_BATCH_TYPES.BILL_TO_EE_TRACKING,
      EXPENSES_BATCH_TYPES.BLACKOUT,
      EXPENSES_BATCH_TYPES.CLIENT_INTEREST,
      EXPENSES_BATCH_TYPES.CODING_CORRECTION,
      EXPENSES_BATCH_TYPES.JOURNAL_ENTRY,
      EXPENSES_BATCH_TYPES.ON_HOLD
    ].map(x => String(x).toLowerCase())
  );

  const isBankFlow = BANK_FLOW.has(batchTypeId);
  const isCheckFlow = CHECK_FLOW.has(batchTypeId);
  const isNoPayment = NO_PAYMENT_FLOW.has(batchTypeId);

  const payeeDisplay = rpInfo?.payToVendor ? rpInfo?.vendorName : rpInfo?.authName;

  return (
    <ThemeProvider theme={theme}>
      <Card className={classes.card} sx={{ mt: 1 }}>
        <Box>
          <CardContent className={classes.cardContainer}>
            <div className="row justify-space-between align-center">
              <div className={classes.iconRow}>
                <Tooltip title="Payment Info" placement="right">
                  <FontAwesomeIcon icon={['fas', 'dollar-sign']} size="2x" />
                </Tooltip>
                <Typography variant="h5" sx={{ pl: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                  Payment Info
                </Typography>
              </div>
            </div>
          </CardContent>

          <CardContent sx={{ p: '10px', ml: '10px', pt: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', pb: '10px' }}>
              <Typography>{payeeDisplay || ''}</Typography>
            </Box>

            {isNoPayment && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2">The selected batch type doesn’t require bank or mailing information.</Typography>
              </Box>
            )}

            {isBankFlow && !isNoPayment && (
              <>
                <Box sx={{ p: '10px', pt: 0 }}>
                  <Typography variant="body2">This batch type is paid via ACH/Wire. Select a configured bank account below.</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', p: '10px' }}>
                  <Box sx={{ mr: 2, pt: '16px' }}>
                    <Typography variant="body2">Pay To (Bank Account)</Typography>
                  </Box>
                  <Box sx={{ mr: 2 }}>
                    <Select
                      value={banking?.bankId ?? ''}
                      onChange={e => patchBanking({ bankId: e.target.value })}
                      disabled={isReadOnly}
                      displayEmpty
                      sx={{ maxWidth: 350, minWidth: 250 }}
                    >
                      <MenuItem value="" disabled>
                        Select a bank account…
                      </MenuItem>
                      {banks.map(b => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', px: '10px' }}>
                  <FormControl>
                    <FormControlLabel
                      label="Wire Transfer"
                      control={<Checkbox disabled={isReadOnly} checked={!!rpi.isWireTransfer} />}
                      onChange={(_, checked) => patchRPI({ isWireTransfer: checked })}
                    />
                  </FormControl>
                </Box>
              </>
            )}
            {isCheckFlow && !isNoPayment && (
              <>
                <Box sx={{ p: '10px', pb: 0 }}>
                  <Typography variant="body2">Mailing Address</Typography>
                </Box>

                <Box sx={{ p: '10px' }}>
                  <Select
                    value={address?.addressId ?? ''}
                    onChange={e => patchAddress({ addressId: e.target.value })}
                    disabled={isReadOnly}
                    displayEmpty
                    sx={{ minWidth: 350 }}
                  >
                    <MenuItem value="" disabled>
                      Select an address…
                    </MenuItem>
                    {addresses.map(a => (
                      <MenuItem key={a.id} value={a.id}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{a.lines}</pre>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </>
            )}
          </CardContent>
        </Box>
      </Card>
    </ThemeProvider>
  );
};

export default RecurringPayablePaymentInfoCard;
